/**
 * Parent-document VoiceBot envelope instrumentation.
 * Records React-side startup milestones and long tasks. Does not import
 * VoiceBot runtime internals. Production may capture; synthetic load lives
 * in voiceBotSimHarness.js and is development-only.
 */

import { collectMemoryResidency, PRODUCT_STAGES } from './voiceBotJourney';
import { collectResourceInventory, getActualResidency, resetVoiceBotSurfacesForTests } from './voiceBotSurfaces';

const MAX_LONG_TASKS = 400;
const MAX_STAGE_SAMPLES = 40;
const MAX_LIFECYCLE_SNAPSHOTS = 24;

let originMs = 0;
let started = false;
let capturing = false;
let observer = null;
let residencyNow = null;
const residencyByStage = [];
const milestones = Object.create(null);
const longTasks = [];
let lastMilestone = null;
let firstIdylateSeen = false;
let studioFullyRevealed = false;
let firstUsableMarked = false;
let currentStage = PRODUCT_STAGES.WELCOME;
let currentSubphase = PRODUCT_STAGES.WELCOME;
let journeyMode = false;
const stageEnteredAt = Object.create(null);
const longTaskByStage = Object.create(null);
const memoryByStage = [];
const lifecycleSnapshots = [];
let settledTimers = [];

function nowOffset() {
  if (!originMs) originMs = performance.now();
  return Math.round((performance.now() - originMs) * 100) / 100;
}

function ensureStageBucket(stage) {
  if (!longTaskByStage[stage]) {
    longTaskByStage[stage] = { count: 0, totalDurationMs: 0 };
  }
  return longTaskByStage[stage];
}

function summarizeLongTasks(list) {
  if (!list.length) {
    return {
      count: 0,
      totalDurationMs: 0,
      maxDurationMs: 0,
      buckets: { lt50: 0, lt100: 0, lt200: 0, gte200: 0 },
    };
  }
  let total = 0;
  let max = 0;
  const buckets = { lt50: 0, lt100: 0, lt200: 0, gte200: 0 };
  for (let i = 0; i < list.length; i++) {
    const d = list[i].duration;
    total += d;
    if (d > max) max = d;
    if (d < 50) buckets.lt50++;
    else if (d < 100) buckets.lt100++;
    else if (d < 200) buckets.lt200++;
    else buckets.gte200++;
  }
  return {
    count: list.length,
    totalDurationMs: Math.round(total * 100) / 100,
    maxDurationMs: Math.round(max * 100) / 100,
    buckets,
  };
}

function longTaskRateByStage() {
  const out = {};
  const now = nowOffset();
  Object.keys(longTaskByStage).forEach((stage) => {
    const entered = stageEnteredAt[stage];
    const bucket = longTaskByStage[stage];
    const elapsedMs = entered == null ? 0 : Math.max(1, now - entered);
    out[stage] = {
      count: bucket.count,
      totalDurationMs: Math.round(bucket.totalDurationMs * 100) / 100,
      msPerSecond: Math.round((bucket.totalDurationMs / (elapsedMs / 1000)) * 10) / 10,
    };
  });
  return out;
}

function observeLongTasks() {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const rec = {
          t: nowOffset(),
          duration: Math.round(entry.duration * 100) / 100,
          name: entry.name || 'unknown',
          afterMilestone: lastMilestone,
          stage: currentStage,
        };
        longTasks.push(rec);
        const bucket = ensureStageBucket(currentStage);
        bucket.count++;
        bucket.totalDurationMs += rec.duration;
        if (longTasks.length > MAX_LONG_TASKS) longTasks.shift();
      });
    });
    observer.observe({ entryTypes: ['longtask'] });
  } catch {
    observer = null;
  }
}

export function startVoiceBotEnvelope(options = {}) {
  if (started) return;
  started = true;
  capturing = true;
  journeyMode = !!options.journey;
  originMs = performance.now();
  markVoiceBotEnvelope('parent:envelope-start');
  if (journeyMode) markVoiceBotEnvelope('journey:capture-start');
  observeLongTasks();
}

export function markVoiceBotEnvelope(name) {
  if (!started || !capturing || !name || milestones[name] != null) return milestones[name];
  const t = nowOffset();
  milestones[name] = t;
  lastMilestone = name;
  if (name === 'studio:fully-revealed') {
    studioFullyRevealed = true;
    maybeMarkFirstUsableSpeech();
  }
  return t;
}

export function setEnvelopeProductStage(stage) {
  if (!stage || stage === currentStage) return;
  currentStage = stage;
  if (stageEnteredAt[stage] == null) stageEnteredAt[stage] = nowOffset();
  markVoiceBotEnvelope(`stage:${stage.toLowerCase()}`);
  const mem = collectMemoryResidency();
  const resources = collectResourceInventory();
  memoryByStage.push({
    t: nowOffset(),
    stage,
    ...mem,
    residencyActual: resources.residencyActual,
    iframesDetail: resources.iframes,
    media: resources.media,
  });
  if (memoryByStage.length > MAX_STAGE_SAMPLES) memoryByStage.shift();
  if (residencyNow) {
    residencyByStage.push({ t: nowOffset(), ...residencyNow });
    if (residencyByStage.length > MAX_STAGE_SAMPLES) residencyByStage.shift();
  }
}

export function setEnvelopeSubphase(subphase) {
  if (subphase) currentSubphase = subphase;
}

export function setEnvelopeResidency(snap) {
  residencyNow = snap
    ? { ...snap, actual: getActualResidency() }
    : { actual: getActualResidency() };
}

function maybeMarkFirstUsableSpeech() {
  if (firstUsableMarked || !firstIdylateSeen || !studioFullyRevealed) return;
  firstUsableMarked = true;
  markVoiceBotEnvelope('parent:first-usable-speech');
  markVoiceBotEnvelope('product:first-usable-speech');
}

export function markVoiceBotFirstIdylate() {
  markVoiceBotEnvelope('parent:first-idylate');
  markVoiceBotEnvelope('avatar:first-idylate');
  firstIdylateSeen = true;
  maybeMarkFirstUsableSpeech();
}

export function noteStudioFullyRevealed() {
  studioFullyRevealed = true;
  markVoiceBotEnvelope('studio:fully-revealed');
  maybeMarkFirstUsableSpeech();
}

export function noteTrainerFinishedForEnvelope() {
  markVoiceBotEnvelope('parent:trainer-finished');
  markVoiceBotEnvelope('solar:complete');
}

export function recordLifecycleSnapshot(name) {
  if (!started || !capturing || !name) return null;
  markVoiceBotEnvelope(name);
  const resources = collectResourceInventory();
  const snap = {
    name,
    t: nowOffset(),
    stage: currentStage,
    subphase: currentSubphase,
    ...resources,
  };
  lifecycleSnapshots.push(snap);
  if (lifecycleSnapshots.length > MAX_LIFECYCLE_SNAPSHOTS) lifecycleSnapshots.shift();
  return snap;
}

function clearSettledTimers() {
  settledTimers.forEach((id) => clearTimeout(id));
  settledTimers = [];
}

export function startStudioSettledSnapshots() {
  clearSettledTimers();
  if (!started || !capturing) return;
  settledTimers.push(setTimeout(() => {
    if (capturing) recordLifecycleSnapshot('studio:settled-2s');
  }, 2000));
  settledTimers.push(setTimeout(() => {
    if (capturing) recordLifecycleSnapshot('studio:settled-10s');
  }, 10000));
}

export function isVoiceBotEnvelopeStarted() {
  return started;
}

export function isJourneyCaptureActive() {
  return started && capturing && journeyMode;
}

export function getVoiceBotEnvelopeSnapshot() {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  return {
    kind: 'parent-envelope',
    formatVersion: '1.2',
    recordedAt: new Date().toISOString(),
    sessionDurationMs: nowOffset(),
    journeyMode,
    currentStage,
    currentSubphase,
    milestones: { ...milestones },
    longTasks: summarizeLongTasks(longTasks),
    longTaskRateByStage: longTaskRateByStage(),
    recentLongTasks: longTasks.slice(-40),
    memoryByStage: memoryByStage.slice(),
    memoryNow: collectMemoryResidency(),
    residencyNow,
    residencyActual: getActualResidency(),
    residencyByStage: residencyByStage.slice(),
    lifecycleSnapshots: lifecycleSnapshots.slice(),
    resourceNow: collectResourceInventory(),
    hardware: {
      hardwareConcurrency: nav.hardwareConcurrency || null,
      deviceMemory: nav.deviceMemory || null,
      userAgent: nav.userAgent || null,
      platform: nav.platform || null,
      language: nav.language || null,
    },
  };
}

export function downloadVoiceBotEnvelopeReport(extra) {
  const report = {
    ...getVoiceBotEnvelopeSnapshot(),
    ...extra,
  };
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = journeyMode
    ? `voicebot-journey-parent-${ts}.json`
    : `voicebot-envelope-parent-${ts}.json`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return report;
}

export function postEnvelopeSnapshotToIframe(win, targetOrigin) {
  if (!win || !targetOrigin) return false;
  try {
    win.postMessage(
      { type: 'VB_ENVELOPE_SNAPSHOT', payload: getVoiceBotEnvelopeSnapshot() },
      targetOrigin
    );
    return true;
  } catch {
    return false;
  }
}

export function requestIframeEnvelopeExport(win, targetOrigin) {
  if (!win || !targetOrigin) return false;
  try {
    win.postMessage({ type: 'VB_ENVELOPE_EXPORT' }, targetOrigin);
    return true;
  } catch {
    return false;
  }
}

export function stopVoiceBotEnvelope() {
  capturing = false;
  clearSettledTimers();
  if (observer) {
    try { observer.disconnect(); } catch { /* optional */ }
    observer = null;
  }
  journeyMode = false;
}

export function resetVoiceBotEnvelopeForTests() {
  stopVoiceBotEnvelope();
  started = false;
  capturing = false;
  originMs = 0;
  lastMilestone = null;
  firstIdylateSeen = false;
  studioFullyRevealed = false;
  firstUsableMarked = false;
  currentStage = PRODUCT_STAGES.WELCOME;
  currentSubphase = PRODUCT_STAGES.WELCOME;
  residencyNow = null;
  Object.keys(milestones).forEach((k) => { delete milestones[k]; });
  Object.keys(longTaskByStage).forEach((k) => { delete longTaskByStage[k]; });
  Object.keys(stageEnteredAt).forEach((k) => { delete stageEnteredAt[k]; });
  longTasks.length = 0;
  memoryByStage.length = 0;
  residencyByStage.length = 0;
  lifecycleSnapshots.length = 0;
  resetVoiceBotSurfacesForTests();
}
